import { ReactNode, useState } from 'react';

import {
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListProps,
  Skeleton,
  Stack,
  Typography,
  styled,
  useTheme,
} from '@mui/material';

import {
  IcAudioFile,
  IcDeleteTrash,
  IcDocument,
  IcPhoto,
  IcVideoRecorder,
} from '@/assets/images';
import { FilePreviewModal } from '@/components';
import { useFetchSasToken } from '@/hooks';
import { IFile, IUser } from '@/models';
import { isDispatcherRole } from '@/services';
import { useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { getFileType, getFileUrlWithValidToken } from '@/utils';

interface FileGridProps extends Omit<ImageListProps, 'children'> {
  files: IFile[];
  cols?: number;
  children?: ReactNode;
  onDelete?: (index: number) => void;
  readOnly?: boolean;
}

const FileGridRoot = styled(ImageList)(({ theme }) => ({
  '& img': {
    width: '100%',
    objectFit: 'cover',
    aspectRatio: '1/1',
    borderRadius: 8,
    cursor: 'pointer',
  },
  '& .file-grid-card': {
    padding: '10px',
    boxShadow: 'none',
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  '& .file-grid-content': {
    padding: 0,
    paddingBottom: '10px',
  },
  '& .file-grid-info': {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
    height: '24px',
  },
  '& .file-grid-typography': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '15px',
    fontWeight: 500,
  },
  '& .file-grid-preview': {
    color: colors.grey[100],
    width: '100%',
    fontWeight: 500,
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export const FileGrid = ({
  files,
  cols = 4,
  children,
  onDelete,
  readOnly = false,
  ...rest
}: FileGridProps) => {
  const [selectedFile, setSelectedFile] = useState<IFile | undefined>(
    undefined,
  );
  const { user } = useUserStore();
  const theme = useTheme();
  const sasToken = useFetchSasToken(files);

  const handleCardClick = (file: IFile) => {
    setSelectedFile(file);
  };

  const renderFileItem = (file: IFile, index: number) => {
    if (!sasToken || !file.url) return <Skeleton key={file.name} />;

    const fileType = getFileType(file.mimeType);

    const fileUrl = getFileUrlWithValidToken(file, sasToken);

    const icons = {
      video: <IcVideoRecorder width={72} />,
      image: <IcPhoto width={34} />,
      audio: <IcAudioFile width={24} height={24} />,
      document: <IcDocument width={24} height={24} />,
    };

    const previewContent: Record<string, ReactNode> = {
      video: (
        <CardMedia
          component="video"
          className="file-grid-preview"
          style={{ aspectRatio: '1/1' }}
          src={`${fileUrl}`}
          controls
        />
      ),
      image: (
        <ImageListItem>
          <img src={`${fileUrl}`} alt={file.name} loading="lazy" />
        </ImageListItem>
      ),
      audio: (
        <Stack
          className="file-grid-preview"
          sx={{
            flex: 1,
            aspectRatio: '1/1',
            backgroundColor: theme.palette.background.default,
          }}
        >
          <IcAudioFile width={72} height={72} />
        </Stack>
      ),
      document: (
        <Stack
          className="file-grid-preview"
          sx={{
            flex: 1,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <IcDocument />
        </Stack>
      ),
    };

    return (
      <Card
        key={file._id ?? file.name ?? index}
        className="file-grid-card"
        onClick={() => handleCardClick(file)}
      >
        <CardContent className="file-grid-content">
          <Stack direction="row" className="file-grid-info">
            <Box display="flex" flex={1} flexDirection="row" overflow="hidden">
              {icons[fileType as keyof typeof icons]}
              <Typography
                variant="body1"
                color="textSecondary"
                className="file-grid-typography"
              >
                {file.name}
              </Typography>
            </Box>
            {!readOnly &&
              onDelete &&
              (user?._id === file.uploadedBy ||
                isDispatcherRole(user as IUser)) && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(index);
                  }}
                >
                  <IcDeleteTrash color="red" />
                </IconButton>
              )}
          </Stack>
        </CardContent>
        {previewContent[fileType]}
      </Card>
    );
  };

  return (
    <FileGridRoot cols={cols} gap={16} {...rest}>
      {children}
      {files?.map((file, index) => renderFileItem(file, index))}
      <FilePreviewModal
        open={Boolean(selectedFile)}
        file={selectedFile}
        onClose={() => setSelectedFile(undefined)}
        readOnly={readOnly}
      />
    </FileGridRoot>
  );
};
